import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FoodCategory } from '../models/IFoodCategory';
import { getFoodCategories, postFoodCategory, translateToEnglish, updateFoodCategory, deleteFoodCategory } from '../services/food';

export default function AddFoodCategoryPage() {
  const [foodCategories, setFoodCategories] = useState<FoodCategory[]>([]);
  const [newfoodCategories, setNewFoodCategories] = useState<FoodCategory>({
    id: '',
    foodCategoryNameTr: '',
    foodCategoryNameEn: '',
    priority: 0,
  });
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [search, setSearch] = useState('');
  const [catImage, setCatImage] = useState<File | null>(null);
  const [catPreview, setCatPreview] = useState<string | null>(null);
  const catFileRef = useRef<HTMLInputElement>(null);

  // Edit modal state
  const [editModal, setEditModal] = useState(false);
  const [editCategory, setEditCategory] = useState<FoodCategory | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editTranslating, setEditTranslating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editCategoryImage, setEditCategoryImage] = useState<File | null>(null);
  const [editCategoryPreview, setEditCategoryPreview] = useState<string | null>(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const editCatFileRef = useRef<HTMLInputElement>(null);

  const handleTranslate = async () => {
    const tr = newfoodCategories.foodCategoryNameTr.trim();
    if (!tr) return;
    setTranslating(true);
    try {
      const res = await translateToEnglish(tr);
      setNewFoodCategories(prev => ({ ...prev, foodCategoryNameEn: res.data }));
    } finally {
      setTranslating(false);
    }
  };

  const handleEditTranslate = async () => {
    if (!editCategory) return;
    const tr = editCategory.foodCategoryNameTr.trim();
    if (!tr) return;
    setEditTranslating(true);
    try {
      const res = await translateToEnglish(tr);
      setEditCategory(prev => prev ? { ...prev, foodCategoryNameEn: res.data } : prev);
    } finally {
      setEditTranslating(false);
    }
  };

  useEffect(() => {
    getAllFoodCategories();
  }, []);

  const getAllFoodCategories = () => {
    getFoodCategories().then((response) => {
      if (response.data) setFoodCategories(response.data);
    });
  };

  const AddFoodCategory = (event: React.FormEvent) => {
    event.preventDefault();
    if (!newfoodCategories.foodCategoryNameTr.trim()) return;
    setSaving(true);
    postFoodCategory(newfoodCategories, catImage ?? undefined)
      .then(() => {})
      .finally(() => {
        setSaving(false);
        clearForm();
      });
  };

  const clearForm = () => {
    setNewFoodCategories({ id: '', foodCategoryNameTr: '', foodCategoryNameEn: '', priority: 0 });
    setCatImage(null);
    setCatPreview(null);
    if (catFileRef.current) catFileRef.current.value = '';
    getAllFoodCategories();
  };

  const openEditModal = (category: FoodCategory) => {
    setEditCategory({ ...category });
    setEditCategoryImage(null);
    setEditCategoryPreview(category.foodCategoryImageUrl ?? null);
    setRemoveExistingImage(false);
    if (editCatFileRef.current) editCatFileRef.current.value = '';
    setConfirmDelete(false);
    setEditModal(true);
  };

  const handleEditImageSelect = (file: File) => {
    setEditCategoryImage(file);
    setEditCategoryPreview(URL.createObjectURL(file));
    setRemoveExistingImage(false);
  };

  const handleEditImageCancel = () => {
    setEditCategoryImage(null);
    setEditCategoryPreview(editCategory?.foodCategoryImageUrl ?? null);
    setRemoveExistingImage(false);
    if (editCatFileRef.current) editCatFileRef.current.value = '';
  };

  const handleEditImageRemove = () => {
    setEditCategoryImage(null);
    setEditCategoryPreview(null);
    setRemoveExistingImage(true);
    if (editCatFileRef.current) editCatFileRef.current.value = '';
  };

  const handleDelete = () => {
    if (!editCategory?.id) return;
    setDeleting(true);
    deleteFoodCategory(editCategory.id)
      .then(() => {
        setEditModal(false);
        setConfirmDelete(false);
        getAllFoodCategories();
      })
      .finally(() => setDeleting(false));
  };

  const handleEditSave = () => {
    if (!editCategory?.id) return;
    setEditSaving(true);
    updateFoodCategory(editCategory.id, {
      foodCategoryNameTr: editCategory.foodCategoryNameTr,
      foodCategoryNameEn: editCategory.foodCategoryNameEn,
      priority: editCategory.priority ?? 0,
    }, editCategoryImage ?? undefined, removeExistingImage)
      .then(() => {
        setEditModal(false);
        getAllFoodCategories();
      })
      .finally(() => setEditSaving(false));
  };

  return (
    <div className="container py-4" style={{ maxWidth: '900px' }}>

      {/* Başlık */}
      <div className="d-flex align-items-center mb-4">
        <div
          style={{
            width: '5px', height: '32px',
            background: '#3b82f6',
            borderRadius: '4px',
            marginRight: '12px',
            flexShrink: 0,
          }}
        />
        <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
          <i className="bi bi-tags-fill me-2 text-primary"></i>
          Kategori Yönetimi
        </h4>
        <span
          className="badge ms-3"
          style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '0.78rem', borderRadius: '20px', padding: '4px 12px' }}
        >
          {foodCategories.length} kategori
        </span>
      </div>

      {/* Ekleme Formu */}
      <div
        className="p-4 rounded-3 mb-4"
        style={{
          background: '#f8f9fa',
          border: '1.5px solid #e9ecef',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        }}
      >
        <div className="fw-semibold mb-3" style={{ color: '#444', fontSize: '0.92rem' }}>
          <i className="bi bi-plus-circle-fill text-primary me-2"></i>
          Yeni Kategori Ekle
        </div>
        <form onSubmit={AddFoodCategory}>
          <div className="row g-3 align-items-end mb-3">
            <div className="col-md-5">
              <label className="form-label small text-muted fw-semibold mb-1">
                <i className="bi bi-translate me-1"></i>Kategori Adı (TR) <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ borderRadius: '10px', border: '1.5px solid #dee2e6' }}
                placeholder="ör. Ana Yemek"
                value={newfoodCategories.foodCategoryNameTr}
                onChange={(e) => setNewFoodCategories(prev => ({ ...prev, foodCategoryNameTr: e.target.value }))}
                required
              />
            </div>
            <div className="col-md-5">
              <label className="form-label small text-muted fw-semibold mb-1 d-flex align-items-center gap-2">
                <span><i className="bi bi-translate me-1"></i>Kategori Adı (EN)</span>
                <button
                  type="button"
                  className="translate-btn"
                  onClick={handleTranslate}
                  disabled={translating || !newfoodCategories.foodCategoryNameTr.trim()}
                >
                  {translating ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                  Çevir
                </button>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ borderRadius: '10px', border: '1.5px solid #dee2e6' }}
                placeholder="ör. Main Course"
                value={newfoodCategories.foodCategoryNameEn}
                onChange={(e) => setNewFoodCategories(prev => ({ ...prev, foodCategoryNameEn: e.target.value }))}
              />
            </div>
          </div>

          {/* Kategori Görseli */}
          <div className="mb-3">
            <label className="form-label small text-muted fw-semibold mb-1">
              <i className="bi bi-image me-1"></i>Kategori Görseli
            </label>
            <div className="d-flex align-items-center gap-3">
              {catPreview ? (
                <div className="cat-img-wrap">
                  <img src={catPreview} alt="preview" className="cat-img-preview" />
                  <button
                    type="button"
                    className="cat-img-remove"
                    onClick={() => { setCatImage(null); setCatPreview(null); if (catFileRef.current) catFileRef.current.value = ''; }}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              ) : (
                <div className="cat-img-placeholder" onClick={() => catFileRef.current?.click()}>
                  <i className="bi bi-cloud-upload"></i>
                </div>
              )}
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm cat-img-select-btn"
                  onClick={() => catFileRef.current?.click()}
                >
                  <i className="bi bi-upload me-1"></i>Resim Seç
                </button>
                <div className="mt-1 cat-img-hint">
                  <i className="bi bi-info-circle me-1"></i>256x256 px formatında PNG/JPG önerilir
                </div>
              </div>
            </div>
            <input
              ref={catFileRef}
              type="file"
              accept="image/png,image/jpeg"
              className="d-none"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) { setCatImage(file); setCatPreview(URL.createObjectURL(file)); }
              }}
            />
          </div>

          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div
              className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
              style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe' }}
            >
              <i className="bi bi-sort-numeric-down text-primary" style={{ fontSize: '0.9rem' }}></i>
              <span className="small fw-semibold" style={{ color: '#1e40af', whiteSpace: 'nowrap' }}>Öncelik</span>
              <select
                className="form-select form-select-sm"
                style={{ width: '70px', borderRadius: '8px', border: '1.5px solid #bfdbfe', background: '#fff', fontWeight: 600, color: '#1e40af' }}
                value={newfoodCategories.priority ?? 0}
                onChange={(e) => setNewFoodCategories(prev => ({ ...prev, priority: Number(e.target.value) }))}
              >
                {Array.from({ length: 11 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
              <span className="small" style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                <i className="bi bi-info-circle me-1"></i>
                0 en başta · 10 en sonda
              </span>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ borderRadius: '10px', fontWeight: 600, height: '38px', paddingInline: '24px' }}
              disabled={saving}
            >
              {saving
                ? <span className="spinner-border spinner-border-sm" role="status" />
                : <><i className="bi bi-plus-lg me-1"></i>Ekle</>}
            </button>
          </div>
        </form>
      </div>

      {/* Kategori Listesi */}
      <div
        className="rounded-3 p-3"
        style={{
          border: '1.5px solid #e9ecef',
          background: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-3 mb-3 flex-wrap">
          <div className="fw-semibold" style={{ color: '#374151', fontSize: '0.9rem' }}>
            <i className="bi bi-list-ul text-primary me-2"></i>
            Mevcut Kategoriler
          </div>
          <div className="position-relative" style={{ minWidth: '220px' }}>
            <i className="bi bi-search position-absolute" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.85rem', pointerEvents: 'none' }}></i>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ borderRadius: '10px', border: '1.5px solid #dee2e6', paddingLeft: '30px', paddingRight: search ? '30px' : undefined }}
              placeholder="Kategori ara..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                type="button"
                className="btn btn-link position-absolute p-0"
                style={{ right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.85rem', lineHeight: 1 }}
                onClick={() => setSearch('')}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
        </div>

        {foodCategories.length === 0 ? (
          <div className="text-center text-muted py-4 small">
            <i className="bi bi-inbox" style={{ fontSize: '2rem', opacity: 0.35 }}></i>
            <div className="mt-2">Henüz kategori eklenmemiş.</div>
          </div>
        ) : foodCategories.filter(c =>
            !search.trim() ||
            c.foodCategoryNameTr.toLowerCase().includes(search.toLowerCase()) ||
            c.foodCategoryNameEn.toLowerCase().includes(search.toLowerCase())
          ).length === 0 ? (
          <div className="text-center text-muted py-4 small">
            <i className="bi bi-search" style={{ fontSize: '2rem', opacity: 0.35 }}></i>
            <div className="mt-2">"{search}" ile eşleşen kategori bulunamadı.</div>
          </div>
        ) : (
          <div className="row g-2">
            {foodCategories
              .filter(c =>
                !search.trim() ||
                c.foodCategoryNameTr.toLowerCase().includes(search.toLowerCase()) ||
                c.foodCategoryNameEn.toLowerCase().includes(search.toLowerCase())
              )
              .map((category, index) => (
              <div key={category.id || index} className="col-6 col-md-4 col-lg-3">
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  onClick={() => openEditModal(category)}
                  style={{
                    background: '#f0f9ff',
                    border: '1.5px solid #bae6fd',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(59,130,246,0.18)')}
                  onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
                >
                  {category.foodCategoryImageUrl ? (
                    <img
                      src={category.foodCategoryImageUrl}
                      alt=""
                      style={{ width: '26px', height: '26px', borderRadius: '6px', objectFit: 'cover', flexShrink: 0 }}
                    />
                  ) : (
                    <i className="bi bi-tag-fill text-primary" style={{ fontSize: '0.8rem', flexShrink: 0 }}></i>
                  )}
                  <div className="flex-grow-1 overflow-hidden">
                    <div className="fw-semibold text-truncate" style={{ color: '#1e40af', lineHeight: 1.3 }}>
                      {category.foodCategoryNameTr}
                    </div>
                    {category.foodCategoryNameEn && (
                      <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                        {category.foodCategoryNameEn}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column align-items-center gap-1" style={{ flexShrink: 0 }}>
                    <span
                      style={{
                        fontSize: '0.68rem', fontWeight: 700,
                        background: '#dbeafe', color: '#1d4ed8',
                        borderRadius: '6px', padding: '1px 6px',
                        lineHeight: 1.6,
                      }}
                    >
                      {category.priority ?? 0}
                    </span>
                    <i className="bi bi-pencil text-primary" style={{ fontSize: '0.68rem', opacity: 0.45 }}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Düzenleme Modalı */}
      <Modal show={editModal} onHide={() => setEditModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: '#eff6ff', borderBottom: '1.5px solid #bfdbfe' }}
        >
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 700, color: '#1e40af' }}>
            <i className="bi bi-pencil-fill me-2"></i>Kategori Düzenle
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {editCategory && (
            <div className="d-flex flex-column gap-3">
              <div>
                <label className="form-label small text-muted fw-semibold mb-1">
                  <i className="bi bi-translate me-1"></i>Kategori Adı (TR) <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ borderRadius: '10px', border: '1.5px solid #dee2e6' }}
                  value={editCategory.foodCategoryNameTr}
                  onChange={(e) => setEditCategory(prev => prev ? { ...prev, foodCategoryNameTr: e.target.value } : prev)}
                />
              </div>

              <div>
                <label className="form-label small text-muted fw-semibold mb-1 d-flex align-items-center gap-2">
                  <span><i className="bi bi-translate me-1"></i>Kategori Adı (EN)</span>
                  <button
                    type="button"
                    className="translate-btn"
                    onClick={handleEditTranslate}
                    disabled={editTranslating || !editCategory.foodCategoryNameTr.trim()}
                  >
                    {editTranslating ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-translate"></i>}
                    Çevir
                  </button>
                </label>
                <input
                  type="text"
                  className="form-control"
                  style={{ borderRadius: '10px', border: '1.5px solid #dee2e6' }}
                  value={editCategory.foodCategoryNameEn}
                  onChange={(e) => setEditCategory(prev => prev ? { ...prev, foodCategoryNameEn: e.target.value } : prev)}
                />
              </div>

              {/* Kategori Görseli */}
              <div>
                <label className="form-label small text-muted fw-semibold mb-1">
                  <i className="bi bi-image me-1"></i>Kategori Görseli
                </label>
                <div className="d-flex align-items-center gap-3">
                  {editCategoryPreview ? (
                    <div className="cat-img-wrap">
                      <img src={editCategoryPreview} alt="preview" className="cat-img-preview" />
                      <button
                        type="button"
                        className="cat-img-remove"
                        onClick={editCategoryImage ? handleEditImageCancel : handleEditImageRemove}
                        title={editCategoryImage ? 'Seçilen yeni resmi iptal et' : 'Resmi kaldır'}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  ) : (
                    <div className="cat-img-placeholder" onClick={() => editCatFileRef.current?.click()}>
                      <i className="bi bi-cloud-upload"></i>
                    </div>
                  )}
                  <div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm cat-img-select-btn"
                      onClick={() => editCatFileRef.current?.click()}
                    >
                      <i className="bi bi-upload me-1"></i>{editCategoryPreview ? 'Resmi Değiştir' : 'Resim Seç'}
                    </button>
                    <div className="mt-1 cat-img-hint">
                      <i className="bi bi-info-circle me-1"></i>
                      {editCategoryImage
                        ? 'Kaydedince yeni resim yüklenecek'
                        : removeExistingImage
                        ? 'Kaydedince resim kaldırılacak (backend desteği eklenene kadar çalışmayabilir)'
                        : '256x256 px formatında PNG/JPG önerilir'}
                    </div>
                  </div>
                </div>
                <input
                  ref={editCatFileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="d-none"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleEditImageSelect(file);
                  }}
                />
              </div>

              <div>
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 rounded-3"
                  style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', width: 'fit-content' }}
                >
                  <i className="bi bi-sort-numeric-down text-primary" style={{ fontSize: '0.9rem' }}></i>
                  <span className="small fw-semibold" style={{ color: '#1e40af', whiteSpace: 'nowrap' }}>Öncelik</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '70px', borderRadius: '8px', border: '1.5px solid #bfdbfe', background: '#fff', fontWeight: 600, color: '#1e40af' }}
                    value={editCategory.priority ?? 0}
                    onChange={(e) => setEditCategory(prev => prev ? { ...prev, priority: Number(e.target.value) } : prev)}
                  >
                    {Array.from({ length: 11 }, (_, i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                  <span className="small" style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                    <i className="bi bi-info-circle me-1"></i>
                    0 en başta · 10 en sonda
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ background: '#f9fafb', borderTop: '1.5px solid #e5e7eb', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
          {confirmDelete ? (
            <div
              className="d-flex align-items-center justify-content-between gap-2 px-3 py-2 rounded-3"
              style={{ background: '#fef2f2', border: '1.5px solid #fecaca' }}
            >
              <span className="small fw-semibold" style={{ color: '#991b1b' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Bu kategoriyi silmek istediğinizden emin misiniz?
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm"
                  style={{ borderRadius: '8px', background: '#f3f4f6', color: '#374151', fontWeight: 600, border: '1.5px solid #e5e7eb' }}
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Hayır
                </button>
                <button
                  className="btn btn-sm btn-danger"
                  style={{ borderRadius: '8px', fontWeight: 600 }}
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting
                    ? <span className="spinner-border spinner-border-sm" role="status" />
                    : <><i className="bi bi-trash-fill me-1"></i>Evet, Sil</>}
                </button>
              </div>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <button
                className="btn btn-sm"
                style={{ borderRadius: '8px', background: '#fef2f2', color: '#991b1b', fontWeight: 600, border: '1.5px solid #fecaca' }}
                onClick={() => setConfirmDelete(true)}
              >
                <i className="bi bi-trash me-1"></i>Kategoriyi Sil
              </button>
              <div className="d-flex gap-2">
                <button
                  className="btn"
                  style={{ borderRadius: '10px', background: '#f3f4f6', color: '#374151', fontWeight: 600, border: '1.5px solid #e5e7eb' }}
                  onClick={() => setEditModal(false)}
                >
                  <i className="bi bi-x-lg me-1"></i>İptal
                </button>
                <button
                  className="btn btn-primary"
                  style={{ borderRadius: '10px', fontWeight: 600 }}
                  onClick={handleEditSave}
                  disabled={editSaving || !editCategory?.foodCategoryNameTr.trim()}
                >
                  {editSaving
                    ? <span className="spinner-border spinner-border-sm" role="status" />
                    : <><i className="bi bi-check-lg me-1"></i>Kaydet</>}
                </button>
              </div>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
